/**
 * Lifecycle hook for `type='sandbox'` property environments.
 *
 * A sandbox environment is a persistent logical target backed by a transient
 * org-shared instance. This hook drives the Flow Designer's auto-warm flow:
 *
 *  - `ensure` (mutation): get-or-create the backing instance — called once on
 *    designer open and from the chip's Start/Retry actions.
 *  - status query: polls readiness (5s while transitional, 30s when ready —
 *    the ready poll doubles as liveness so expiry surfaces without a reload).
 *  - heartbeat: slides the 30-min TTL every ~5 min while the tab is visible;
 *    paused on document.hidden so a backgrounded tab lets the sandbox expire.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SandboxEnvironmentState, SandboxEnvironmentStatus } from '@chatsdet/types';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

// Re-export the shared types so existing component imports keep resolving.
export type { SandboxEnvironmentState, SandboxEnvironmentStatus };

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

const sandboxBase = (propertyId: string, environmentId: string) =>
  `${API_URL}/org/properties/${propertyId}/environments/${environmentId}/sandbox`;

/**
 * @param propertyId Property the environment belongs to (null disables).
 * @param environmentId The sandbox environment id (null/non-sandbox disables).
 * @param options.enabled Gate — pass `env?.type === 'sandbox'`.
 * @param options.autoEnsure Auto-start the instance on activation (Flow
 *   Designer). Defaults to false — read-only consumers (environments page
 *   badge) must never start instances by rendering.
 */
export function useSandboxEnvironment(
  propertyId: string | null,
  environmentId: string | null,
  options: { enabled: boolean; autoEnsure?: boolean }
) {
  const { enabled, autoEnsure = false } = options;
  const queryClient = useQueryClient();
  const active = enabled && !!propertyId && !!environmentId;

  const statusQuery = useQuery({
    queryKey: ['sandbox-environment-status', environmentId],
    enabled: active,
    queryFn: async (): Promise<SandboxEnvironmentState> => {
      const res = await fetch(`${sandboxBase(propertyId!, environmentId!)}/status`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch sandbox status');
      const json = (await res.json()) as { sandbox: SandboxEnvironmentState };
      return json.sandbox;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'warming' || status === 'stopping') return 5000;
      // Ready/asleep/failed: slow poll keeps expiry + external starts visible.
      return 30_000;
    },
  });

  const ensureMutation = useMutation({
    mutationFn: async (): Promise<SandboxEnvironmentState> => {
      const res = await fetch(`${sandboxBase(propertyId!, environmentId!)}/ensure`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to start sandbox');
      const json = (await res.json()) as { sandbox: SandboxEnvironmentState };
      return json.sandbox;
    },
    onSuccess: (sandbox) => {
      queryClient.setQueryData(['sandbox-environment-status', environmentId], sandbox);
    },
  });

  const stopMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await fetch(`${sandboxBase(propertyId!, environmentId!)}/instance`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to stop sandbox');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['sandbox-environment-status', environmentId],
      });
    },
  });

  // Auto-ensure exactly once per (property, environment) activation.
  const ensuredKeyRef = useRef<string | null>(null);
  const ensure = ensureMutation.mutate;
  useEffect(() => {
    if (!active || !autoEnsure) return;
    const key = `${propertyId}:${environmentId}`;
    if (ensuredKeyRef.current === key) return;
    ensuredKeyRef.current = key;
    ensure();
  }, [active, autoEnsure, propertyId, environmentId, ensure]);

  // Heartbeat: slide the TTL while the tab is open and visible. Fire-and-
  // forget; a refused slide (terminal / max lifetime) surfaces via the next
  // status poll, not as an error toast. Only the auto-ensure consumer (the
  // designer) holds the sandbox open — read-only badges never heartbeat.
  const status = statusQuery.data?.status;
  useEffect(() => {
    if (!active || !autoEnsure || status !== 'ready') return;
    const beat = () => {
      if (document.hidden) return;
      void fetch(`${sandboxBase(propertyId!, environmentId!)}/heartbeat`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => undefined);
    };
    const timer = setInterval(beat, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [active, autoEnsure, status, propertyId, environmentId]);

  const retry = useCallback(() => ensureMutation.mutate(), [ensureMutation]);

  return {
    /** Null while loading or when disabled. */
    sandbox: active ? (statusQuery.data ?? null) : null,
    isLoading: active && statusQuery.isLoading,
    isEnsuring: ensureMutation.isPending,
    isStopping: stopMutation.isPending,
    /** Start (asleep) or retry (failed) the backing instance. */
    start: retry,
    /** Collapse the TTL — teardown fires immediately. */
    stop: stopMutation.mutate,
  };
}
